import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { priceHint } from '@domain/list'
import type { ListItem } from '@domain/list'
import { baseQuantity, formatSize } from '@domain/product'
import type { Product } from '@domain/product'
import { formatBRL } from '@shared/money/format'
import { useSegurar } from '@ui/shell/segurar'
import { civilDateOf, timeInHousehold } from '@shared/date/timezone'

/**
 * Uma linha da lista.
 *
 * ## Os toques que importam ficam grandes, e os que não, pequenos
 *
 * *"Adicionar um item leva menos toques do que abrir o app de notas do
 * sistema"* — a primeira linha da definição de pronto. Marcar como comprado é o
 * gesto do corredor do mercado, com uma mão só: é a **linha inteira**, não uma
 * caixinha de 16 pixels. Mover e apagar são raros, e ficam nos ícones pequenos
 * da direita.
 *
 * ## Item sem vínculo NÃO é tratado como incompleto
 *
 * Última linha da definição de pronto. Não há aviso, badge ou espaço vazio
 * reservado para o preço: quem não tem produto vinculado simplesmente não tem
 * aquela linha. É a diferença entre um campo opcional e um campo pendente.
 *
 * ## O círculo MARCA, o corpo NAVEGA (decisão 218)
 *
 * Antes a linha inteira era um `<button>` de marcar, e por isso o grupo só
 * podia aparecer como texto: `<a>` dentro de `<button>` é HTML inválido, e um
 * leitor de tela não sabe qual dos dois anunciar. O preço era perder a
 * comparação de `/grupos/$id` bem onde ela é útil — no corredor do mercado.
 *
 * Separando os dois, os dois cabem: o círculo virou o alvo de marcar, e o
 * corpo virou o link. O gesto de uma mão só continua existindo — o círculo tem
 * 44px, e a linha ficou com **dois** destinos previsíveis em vez de um só.
 *
 * Item de texto livre não tem para onde ir, e aí o corpo é `<span>`: um link
 * que não leva a lugar nenhum é pior do que texto.
 *
 * ## Os três gestos, e o que cada um custa (decisão 220)
 *
 * **Arrastar pela alça** reordena. Só pela alça: se a linha inteira arrastasse,
 * o dedo perderia o rolar da página, que é o gesto mais usado da tela.
 *
 * **Deslizar para a esquerda revela "Remover"** — revela, não apaga. No iPhone
 * o gesto é seguro porque existe desfazer; aqui `removeItem` é `deleteDoc`, sem
 * lixeira. O deslize parcial que descobre um botão vermelho é o mesmo idioma e
 * exige um toque deliberado. Deslize que apaga direto seria um item perdido por
 * bolso.
 *
 * A célula **anda um pouco** para a esquerda junto — o empurrão é o que faz o
 * gesto ser um deslize em vez de uma troca de botões. Pouco, e não os 96px de
 * uma linha inteira: o botão **toma o lugar** dos ícones da direita, em vez de
 * a linha inteira deslizar. Deslizar é o que o iPhone faz, e foi o que tentei primeiro: o
 * conteúdo saiu pela borda esquerda e a linha ficou um retângulo vazio com um
 * botão vermelho no fim. Num `<li>` de 375px com três ícones não sobra a
 * largura que aquele gesto pressupõe — e continuar lendo o nome do item
 * enquanto se decide apagá-lo importa mais do que a fidelidade ao gesto.
 *
 * **Segurar abre o menu** com o que não cabe na linha. O mesmo menu abre por
 * toque ou Enter na alça: as setas ↑↓ saíram, e sem essa porta quem usa teclado
 * ou leitor de tela ficaria sem reordenar.
 */

type Props = {
  item: ListItem
  product: Product | null
  /** Preenchido quando o item foi adicionado por sugestão de grupo. */
  grupo: { tamanhos: number } | null
  /** Para onde o corpo leva. `null` em item de texto livre. */
  destino: { to: '/grupos/$id' | '/produtos/$id'; params: { id: string } } | null
  onToggle: () => void
  onRemove: () => void
  onSchedule: () => void
  /** Long-press na linha, e toque/Enter na alça, abrem o mesmo menu. */
  onAbrirMenu: () => void
  /** O `pointerdown` do `useArrastar`. O resto do gesto o hook ouve no window. */
  onComecarArraste: (e: React.PointerEvent) => void
  /** Quantos pixels esta linha desloca durante um arrasto em curso. */
  deslocamento: number
  arrastando: boolean
}

/** O quanto o dedo anda para a esquerda antes de "Remover" aparecer. */
const LIMIAR_DESLIZE = 56

export function ItemRow({
  item,
  product,
  grupo,
  destino,
  onToggle,
  onRemove,
  onSchedule,
  onAbrirMenu,
  onComecarArraste,
  arrastando,
  deslocamento,
}: Props) {
  const [revelado, setRevelado] = useState(false)
  // O segurar mora em `useSegurar` desde que a lista de transações passou a
  // usá-lo (decisão 264). O DESLIZE fica aqui: ele é dos lembretes, e monta-se
  // sobre o mesmo movimento que o gancho já mede.
  const segurar = useSegurar(onAbrirMenu)

  function aoArrastarDedo(e: React.PointerEvent) {
    const d = segurar.aoMover(e)
    if (d === null) return

    // Vertical vence: a pessoa está rolando a página, e sequestrar isso para
    // revelar um botão é o defeito clássico do deslize mal feito.
    if (Math.abs(d.dy) > Math.abs(d.dx)) {
      segurar.encerrar()
      return
    }
    if (d.dx < -LIMIAR_DESLIZE) {
      segurar.suprimirProximoClique()
      setRevelado(true)
      segurar.encerrar()
    } else if (d.dx > LIMIAR_DESLIZE) {
      setRevelado(false)
      segurar.encerrar()
    }
  }
  const preco =
    product === null
      ? null
      : priceHint(product.stats.lastPriceCents, menorPago(product), formatBRL)

  /**
   * O tamanho da embalagem vinculada.
   *
   * Fica **abaixo do preço e mais apagado que ele**: no corredor do mercado a
   * pergunta é "quanto custa", e o tamanho só entra quando há duas caixas na
   * prateleira. Dar-lhe o mesmo peso do preço faria a linha ter dois números
   * disputando — e o que decide a compra é o de cima.
   *
   * Produto recém-criado não tem tamanho, e aí a linha simplesmente não existe.
   */
  const tamanho =
    product === null ? null : formatSize(product.size, product.unit, product.packQuantity)

  const miolo = (
    <>
      <span
        className={`block text-sm ${item.completed ? 'text-[var(--text-muted)] line-through' : ''}`}
      >
        {item.text}
        {item.quantity !== null && (
          <span className="text-[var(--text-muted)]">
            {` · ${String(item.quantity)}${item.unit ?? ''}`}
          </span>
        )}
      </span>
      {preco !== null && !item.completed && (
        <span className="block text-xs tabular-nums text-[var(--text-muted)]">{preco}</span>
      )}
      {tamanho !== null && !item.completed && (
        <span className="block text-[11px] tabular-nums text-[var(--text-faint)]">{tamanho}</span>
      )}
      {/* Nunca repete o texto do item: ele já É o nome do grupo, porque a
          sugestão grava com esse nome. O que acrescenta é dizer que há um
          grupo, quantas embalagens ele tem, e — pelo "›" — que dá para ir lá. */}
      {grupo !== null && !item.completed && (
        <span className="block text-xs text-[var(--text-faint)]">
          {grupo.tamanhos > 1
            ? `grupo · ${String(grupo.tamanhos)} tamanhos ›`
            : 'grupo ›'}
        </span>
      )}
      {item.dueAt !== null && !item.completed && (
        <span className="block text-xs text-[var(--text-muted)]">
          {`⏰ ${diaEHora(item.dueAt)}`}
        </span>
      )}
    </>
  )

  return (
    <li
      // O hook do arrastar procura por este atributo para medir as linhas.
      data-item-id={item.id}
      // O arrastado segue o dedo SEM transição (1:1, senão dá borracha); os
      // outros abrem espaço COM transição, que é o movimento suave que se vê.
      style={{
        transform: deslocamento === 0 ? undefined : `translateY(${String(deslocamento)}px)`,
        transition: arrastando ? 'none' : undefined,
      }}
      className={`relative overflow-hidden border-b border-[var(--border)] motion-safe:transition-transform ${
        arrastando ? 'z-10 opacity-80 shadow-lg' : ''
      }`}
    >
      <div
        onPointerDown={segurar.aoApertar}
        onPointerMove={aoArrastarDedo}
        onPointerUp={segurar.encerrar}
        onPointerCancel={segurar.encerrar}
        // Segurar em cima de um link abre o menu do sistema no celular e
        // seleciona texto no desktop. Os dois competem com o nosso menu.
        onContextMenu={(e) => {
          e.preventDefault()
        }}
        onClickCapture={segurar.aoClicarNaCaptura}
        // O empurrão do deslize vai por `style` porque `-translate-x-2` não é
        // gerada como utility aqui — medido no navegador: a classe ficava no
        // elemento e nenhuma regra existia para ela.
        //
        // `sem-preview` está declarada no `<style>` do `index.html`, e o
        // comentário de lá explica por que não pode morar no CSS nem no
        // `style` inline.
        style={{ transform: revelado ? 'translateX(-8px)' : undefined }}
        className="sem-preview relative flex touch-pan-y items-center gap-2 py-2 select-none motion-safe:transition-transform"
      >
        {/* O alvo de marcar. Antes era a linha inteira; virou o círculo para o
            corpo poder navegar. 44px pela classe `alvo`, mesmo o desenho sendo
            um círculo de 24. */}
        <button
          type="button"
          onClick={onToggle}
          className="alvo shrink-0"
          // Numa lista de compras o gesto de cada linha é marcar: ↑↓ andam
          // pelos círculos, e Enter marca.
          data-nav-linha
          aria-pressed={item.completed}
          // O rótulo é explícito porque o texto do item já aparece nos rótulos
          // dos botões vizinhos: sem ele, um leitor de tela — e o teste — leem
          // vários botões chamados "X" na mesma linha.
          aria-label={`${item.completed ? 'Desmarcar' : 'Marcar'} ${item.text}`}
        >
          <span
            aria-hidden="true"
            className={`grid h-6 w-6 place-items-center rounded-full border ${
              item.completed
                ? 'border-transparent bg-[var(--text)] text-[var(--bg)]'
                : 'border-[var(--border)]'
            }`}
          >
            {item.completed ? '✓' : ''}
          </span>
        </button>

        {destino === null ? (
          <span className="min-w-0 flex-1">{miolo}</span>
        ) : (
          <Link {...destino} className="min-w-0 flex-1" aria-label={`Abrir ${item.text}`}>
            {miolo}
          </Link>
        )}

        {!item.completed && revelado && (
          <button
            type="button"
            onClick={onRemove}
            // `motion-safe` porque quem pediu menos movimento no sistema não
            // quer um botão entrando deslizando.
            className="alvo shrink-0 rounded-lg bg-[var(--negative)] px-3 text-sm font-medium text-[var(--bg)] motion-safe:animate-[surgir_140ms_ease-out]"
            aria-label={`Remover ${item.text}`}
          >
            Remover
          </button>
        )}

        {!item.completed && !revelado && (
          <>
            <button
              type="button"
              onClick={onSchedule}
              className="alvo shrink-0 text-xs text-[var(--text-muted)]"
              aria-label={`Lembrete de ${item.text}`}
            >
              ⏰
            </button>

            {/* A alça. `touch-none` só AQUI: sem isso o navegador rola a página
                em vez de deixar o `pointermove` chegar, e o item não sai do
                lugar. Na linha inteira isso mataria o rolar. */}
            <button
              type="button"
              onPointerDown={onComecarArraste}
              onClick={onAbrirMenu}
              className="alvo shrink-0 touch-none text-sm text-[var(--text-muted)]"
              aria-label={`Reordenar ${item.text}`}
            >
              ⠿
            </button>
          </>
        )}

        {item.completed && (
          <button
            type="button"
            onClick={onRemove}
            className="alvo shrink-0 text-xs text-[var(--text-muted)]"
            aria-label={`Apagar ${item.text}`}
          >
            ✕
          </button>
        )}
      </div>
    </li>
  )
}

/**
 * O menor preço **em reais da embalagem**, e não por unidade base.
 *
 * `stats.minPerBaseUnitMilliCents` é por mililitro; ao lado de "Último R$ 4,29"
 * ele daria "Menor R$ 0,01", que é verdadeiro e ilegível. A conversão de volta
 * exigiria o tamanho — e produto sem tamanho declarado não tem nem o mínimo. Por
 * ora, sem tamanho não há "menor", que é o lado honesto: melhor não dizer do que
 * dizer um centavo.
 */
function menorPago(product: Product): number | null {
  const { minPerBaseUnitMilliCents } = product.stats
  if (minPerBaseUnitMilliCents === null) return null
  if (product.size === null || product.unit === null) return null
  // `baseQuantity`, e não `size * packQuantity`: um produto em litros tem
  // `size: 1` e mil unidades base, e multiplicar pelo tamanho cru daria um
  // preço mil vezes menor.
  const base = baseQuantity(product.size, product.unit, product.packQuantity)
  return Math.round((minPerBaseUnitMilliCents * base) / 1000)
}

/**
 * `'2026-09-18T21:00:00Z'` → `'18/09 às 18:00'`, **no fuso da casa**.
 *
 * O dia sai de `civilDateOf`, e não de `toISOString().slice()`: um lembrete às
 * 22:00 de São Paulo é 01:00 do dia seguinte em UTC, e o corte mostraria a data
 * errada exatamente nos lembretes de fim de noite.
 */
function diaEHora(iso: string): string {
  const instante = new Date(iso)
  const dia = civilDateOf(instante)
  return `${dia.slice(8, 10)}/${dia.slice(5, 7)} às ${timeInHousehold(instante)}`
}
